import { BaseQueryFn } from '@reduxjs/toolkit/dist/query/react';
import axios, { AxiosError, AxiosProgressEvent, AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * Creates a custom RTK Query BaseQueryFn factory that wraps an axios call with optional header-prep and consistent error handling, returning the deserialized response data or a { status, data } error object from the catch.
 *
 * @param baseUrl - The base URL to prepend to each request.
 */
export const axiosBaseQuery =
  ({
    baseUrl,
    prepareHeaders
  }: {
    baseUrl: string;
    prepareHeaders?: (headers: Record<string, string>) => Promise<Record<string, string>>;
  }): BaseQueryFn<
    {
      url: string;
      method: AxiosRequestConfig['method'];
      data?: AxiosRequestConfig['data'];
      params?: AxiosRequestConfig['params'];
      onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
    },
    unknown,
    unknown
  > =>
  async ({ url, method, data, params, onUploadProgress }) => {
    try {
      let headers: Record<string, string> = {};
      if (prepareHeaders) {
        headers = await prepareHeaders(headers);
      }

      const axiosParams = {
        url: baseUrl + '/' + url,
        method,
        data,
        params,
        headers,
        onUploadProgress
      };

      const result: AxiosResponse = await axios(axiosParams);
      return { data: result.data, onUploadProgress };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message
        }
      };
    }
  };
